import React, { useState } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { 
  FileText, BookOpen, Layers, GitBranch, FileEdit,
  Calendar, Edit2, Save, X, Loader2, ChevronRight, User
} from 'lucide-react';
import { toast } from 'sonner';
import ReferencesSection from '../../components/ReferencesSection';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

import { API_BASE } from '../../config/api';
const API = `${API_BASE}`;

const OverviewPage = () => {
  const { conlang, setConlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [aboutText, setAboutText] = useState(conlang?.about || '');
  const [saving, setSaving] = useState(false);
  const [editingCreator, setEditingCreator] = useState(false);
  const [creatorText, setCreatorText] = useState(conlang?.creator || '');

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSaveAbout = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/conlangs/${conlang.conlang_id}`,
        { about: aboutText },
        { withCredentials: true }
      );
      setConlang(response.data);
      setEditing(false);
      toast.success('About section updated');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setAboutText(conlang?.about || '');
    setEditing(false);
  };

  const handleSaveCreator = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/conlangs/${conlang.conlang_id}`,
        { creator: creatorText },
        { withCredentials: true }
      );
      setConlang(response.data);
      setEditingCreator(false);
      toast.success('Creator updated');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const stats = [
    { 
      label: 'Phonemes', 
      value: conlang?.phoneme_count || 0, 
      icon: FileText, 
      color: 'text-secondary',
      bgColor: 'bg-secondary/20',
      description: 'Consonants & vowels defined',
      link: `/conlang/${conlangId}/phonology`
    },
    { 
      label: 'Words', 
      value: conlang?.word_count || 0, 
      icon: BookOpen, 
      color: 'text-tertiary',
      bgColor: 'bg-tertiary/20',
      description: 'Lexicon entries',
      link: `/conlang/${conlangId}/lexicon`
    },
    { 
      label: 'Morphology Rules', 
      value: conlang?.morphology_rules_count || 0, 
      icon: Layers, 
      color: 'text-primary',
      bgColor: 'bg-primary/20',
      description: 'Word formation patterns',
      link: `/conlang/${conlangId}/morphology`
    },
    { 
      label: 'Syntax Rules', 
      value: conlang?.syntax_rules_count || 0, 
      icon: GitBranch, 
      color: 'text-quaternary',
      bgColor: 'bg-quaternary/20',
      description: 'Sentence structures',
      link: `/conlang/${conlangId}/syntax`
    },
    { 
      label: 'Texts', 
      value: conlang?.text_count || 0, 
      icon: FileEdit, 
      color: 'text-green-500',
      bgColor: 'bg-green-500/20',
      description: 'Sample texts & translations',
      link: `/conlang/${conlangId}/texts`
    },
  ];

  return (
    <div className="max-w-4xl space-y-6">
      {/* Page Header - Language Name and Short Description */}
      <div>
        <h2 className="font-heading text-3xl font-bold text-foreground" data-testid="overview-title">
          {conlang?.name}
        </h2>
        {conlang?.native_name && (
          <p className="font-conlang text-secondary text-lg mt-1">
            {conlang.native_name}
          </p>
        )}
        {conlang?.description && (
          <p className="text-foreground-muted mt-2">
            {conlang.description}
          </p>
        )}
        {/* Creator & Documenter */}
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <User className="w-3.5 h-3.5 text-foreground-subtle" />
            <span className="text-foreground-subtle">Creator:</span>
            {editingCreator ? (
              <div className="flex items-center gap-1">
                <input
                  value={creatorText}
                  onChange={e => setCreatorText(e.target.value)}
                  className="bg-background border border-border rounded px-2 py-0.5 text-sm text-foreground w-64"
                  placeholder="e.g. J.R.R. Tolkien"
                  data-testid="creator-input"
                />
                <button onClick={handleSaveCreator} disabled={saving} className="text-primary hover:text-primary/80" data-testid="save-creator-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => { setCreatorText(conlang?.creator || ''); setEditingCreator(false); }} className="text-foreground-subtle hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <span className="text-foreground-muted group flex items-center gap-1">
                {conlang?.creator || <span className="italic text-foreground-subtle">Not set</span>}
                <button onClick={() => { setCreatorText(conlang?.creator || ''); setEditingCreator(true); }} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid="edit-creator-btn">
                  <Edit2 className="w-3 h-3 text-foreground-subtle" />
                </button>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <FileEdit className="w-3.5 h-3.5 text-foreground-subtle" />
            <span className="text-foreground-subtle">Documented by:</span>
            <span className="text-foreground-muted">{conlang?.owner_name || user?.name || 'Unknown'}</span>
          </div>
          {conlang?.source && (
            <div className="flex items-center gap-2 text-sm">
              <BookOpen className="w-3.5 h-3.5 text-foreground-subtle" />
              <span className="text-foreground-subtle">Imported from:</span>
              <span className="text-foreground-muted">{conlang.source.language_name}</span>
              {conlang.source.creator_name && (
                <span className="text-foreground-subtle">by {conlang.source.creator_name}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* About Section - Longer Description */}
      <Card className="bg-background-paper border-border" data-testid="about-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-xl text-foreground">
            About
          </CardTitle>
          {!editing && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-about-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-4">
              <Textarea
                value={aboutText}
                onChange={(e) => setAboutText(e.target.value)}
                placeholder="Describe your language... What inspired it? What culture speaks it? What makes it unique?"
                className="min-h-[200px] bg-background border-border text-foreground"
                data-testid="about-textarea"
              />
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={handleCancelEdit}
                  className="border-border"
                  data-testid="cancel-about-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveAbout}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-about-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="prose prose-invert max-w-none">
              {conlang?.about ? (
                <p className="text-foreground whitespace-pre-wrap" data-testid="about-text">
                  {conlang.about}
                </p>
              ) : (
                <p className="text-foreground-subtle italic" data-testid="about-placeholder">
                  No description yet. Click "Edit" to add information about your language, 
                  its origins, the culture that speaks it, and what makes it unique.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
          Language Statistics
        </h3>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="stats-grid">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={stat.label} 
                className="bg-background-paper border-border hover:border-primary/50 transition-all cursor-pointer group"
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => navigate(stat.link)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-md ${stat.bgColor} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground-muted text-sm group-hover:text-foreground transition-colors">{stat.label}</p>
                      <p className="font-mono text-2xl font-semibold text-foreground">
                        {stat.value}
                      </p>
                      <p className="text-foreground-subtle text-xs mt-1">
                        {stat.description}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-foreground-subtle opacity-0 group-hover:opacity-100 transition-opacity self-center" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Metadata */}
      <Card className="bg-background-paper border-border" data-testid="metadata-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2 text-foreground-muted">
              <Calendar className="w-4 h-4" />
              <span>Created {formatDate(conlang?.created_at)}</span>
            </div>
            <div className="flex items-center gap-2 text-foreground-muted">
              <Calendar className="w-4 h-4" />
              <span>Updated {formatDate(conlang?.updated_at)}</span>
            </div>
            {conlang?.native_name && (
              <div className="flex items-center gap-2">
                <span className="text-foreground-muted">Native name:</span>
                <span className="font-conlang text-secondary">{conlang.native_name}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <ReferencesSection section="overview" />
    </div>
  );
};

export default OverviewPage;
